import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";

// ─── Spinner frames ───────────────────────────────────────────────────────────
const SP = ["⠋","⠙","⠹","⠸","⠼","⠴","⠦","⠧","⠇","⠏"];

// ─── Sequences ────────────────────────────────────────────────────────────────
const SEQUENCES = [
  [
    { k:"cmd", t:"sam init --new-venture" },
    { k:"spin", s:"Scanning market gaps...",      d:"✓ Scanning market gaps..." },
    { k:"spin", s:"Found: ai-receptionist",       d:"✓ Found: ai-receptionist" },
    { k:"spin", s:"Initialising Purple Luna...",  d:"✓ Initialising Purple Luna..." },
    { k:"out",  t:"✓ WhatsApp receptionist live", c:"#4ADE80" },
    { k:"out",  t:"> Luna Voice: in testing",     c:"#C9D1D9" },
  ],
  [
    { k:"cmd", t:"sam run canada-life --ship" },
    { k:"spin", s:"Syncing design system ↔ code...", d:"✓ Design system ↔ code in sync" },
    { k:"spin", s:"Introducing handover tool...",    d:"✓ New design → dev handover tool" },
    { k:"spin", s:"Rebooting delivery processes...", d:"✓ Rebooting delivery processes..." },
    { k:"out",  t:"✓ Design and dev in step",        c:"#4ADE80" },
    { k:"out",  t:`> ${new Date().getFullYear() - 2021} years. Still going.`, c:"#C9D1D9" },
  ],
  [
    { k:"cmd", t:"sam status" },
    { k:"out", t:"ACTIVE PLATES:",                              c:"#8B949E" },
    { k:"out", t:"  ├── Purple Luna ·········  whatsapp AI 🤖", c:"#C9D1D9" },
    { k:"out", t:"  ├── Luna Voice ··········  testing   📞",   c:"#C9D1D9" },
    { k:"out", t:"  ├── Canada Life ·········  shipping  🚀",  c:"#C9D1D9" },
    { k:"out", t:"  ├── The Lab ·············  tinkering  🧪", c:"#C9D1D9" },
    { k:"out", t:"  └── Eva Luna ···········   judging    🐱",  c:"#C9D1D9" },
    { k:"out", t:"> all plates spinning",                      c:"#7EE787" },
  ],
  [
    { k:"cmd", t:"sam --skills" },
    { k:"bar", l:"DESIGN  ", n:20, total:20, sfx:"  figma · systems · ux",  c:"#C9D1D9" },
    { k:"bar", l:"CODE    ", n:16, total:20, sfx:"  react · ts · python",   c:"#C9D1D9" },
    { k:"bar", l:"PRODUCT ", n:20, total:20, sfx:"  strategy · shipping",   c:"#C9D1D9" },
    { k:"bar", l:"MUSIC   ", n:8,  total:20, sfx:"  guitar · bağlama",      c:"#C9D1D9" },
    { k:"bar", l:"SLEEP   ", n:2,  total:20, sfx:"  ...working on it",      c:"#8B949E" },
  ],
  [
    { k:"cmd", t:"git log --oneline -5" },
    { k:"out", t:"a1b2c3d  Fixed that thing at 2am",    c:"#C9D1D9" },
    { k:"out", t:"e4f5g6h  Shipped Luna on WhatsApp",    c:"#C9D1D9" },
    { k:"out", t:"i7j8k9l  Redesigned pension journey", c:"#C9D1D9" },
    { k:"out", t:"m0n1o2p  Added cursor rules for AI",  c:"#C9D1D9" },
    { k:"out", t:"q3r4s5t  Fed the cat  (critical)",    c:"#C9D1D9" },
  ],
];

// Static fallback for prefers-reduced-motion — shows sequence 3 instantly
const STATIC = SEQUENCES[2].map((s, i) => ({
  id: `st${i}`,
  text: s.k === "cmd" ? `$ ${s.t}` : s.t,
  color: s.k === "cmd" ? "#7EE787" : (s.c || "#C9D1D9"),
}));

export default function HeroTerminal() {
  const reduced = useMemo(() =>
    typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  []);
  const isMobile = useMemo(() =>
    typeof window !== "undefined" && !!window.matchMedia?.("(max-width: 768px)").matches,
  []);

  const [lines, setLines]       = useState(reduced ? STATIC : []);
  const [fading, setFading]     = useState(false);
  const [cursorOn, setCursorOn] = useState(true);

  const alive = useRef(true);
  const tids  = useRef([]);

  // Cleanup on unmount
  useEffect(() => () => {
    alive.current = false;
    tids.current.forEach(clearTimeout);
  }, []);

  // Cursor blink
  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => { if (alive.current) setCursorOn(v => !v); }, 530);
    return () => clearInterval(id);
  }, [reduced]);

  // Schedule helper — respects unmount
  const later = (fn, ms) => {
    if (!alive.current) return;
    const id = setTimeout(() => { if (alive.current) fn(); }, ms);
    tids.current.push(id);
  };

  // ─── Animation engine ────────────────────────────────────────────────────
  useEffect(() => {
    if (reduced) return;
    // Reset liveness on every mount/remount (guards against React Strict Mode
    // double-invoke: cleanup sets alive=false, then effect re-runs)
    alive.current = true;
    tids.current = [];
    const seqs = isMobile ? SEQUENCES.slice(0, 3) : SEQUENCES;
    let si = 0;

    function runSeq() {
      if (!alive.current) return;
      const seq = seqs[si % seqs.length];
      si++;

      setFading(false);
      setLines([]);

      let delay = 400;

      seq.forEach((item, idx) => {
        const lid = `${si}-${idx}`;

        if (item.k === "cmd") {
          const full = `$ ${item.t}`;
          for (let c = 0; c < full.length; c++) {
            const jitter = Math.floor(Math.random() * 18);
            const cd     = delay + c * 44 + jitter;
            const slice  = full.slice(0, c + 1);
            const first  = c === 0;
            later(() => setLines(prev =>
              first
                ? [...prev, { id: lid, text: slice, color: "#7EE787" }]
                : prev.map(l => l.id === lid ? { ...l, text: slice } : l)
            ), cd);
          }
          delay += full.length * 46 + 90;

        } else if (item.k === "spin") {
          const cycles = 9;
          for (let f = 0; f < cycles; f++) {
            const frame = SP[f % SP.length];
            const first = f === 0;
            later(() => {
              const txt = `${frame} ${item.s}`;
              setLines(prev =>
                first
                  ? [...prev, { id: lid, text: txt, color: "#8B949E" }]
                  : prev.map(l => l.id === lid ? { ...l, text: txt } : l)
              );
            }, delay + f * 110);
          }
          later(() =>
            setLines(prev => prev.map(l => l.id === lid ? { ...l, text: item.d, color: "#4ADE80" } : l)),
            delay + cycles * 110 + 40
          );
          delay += cycles * 110 + 150;

        } else if (item.k === "out") {
          const full = item.t;
          for (let c = 0; c < full.length; c++) {
            const jitter = Math.floor(Math.random() * 8);
            const cd     = delay + c * 21 + jitter;
            const slice  = full.slice(0, c + 1);
            const first  = c === 0;
            later(() => setLines(prev =>
              first
                ? [...prev, { id: lid, text: slice, color: item.c || "#C9D1D9" }]
                : prev.map(l => l.id === lid ? { ...l, text: slice } : l)
            ), cd);
          }
          delay += full.length * 23 + 55;

        } else if (item.k === "bar") {
          const empty = `${item.l}${"░".repeat(item.total)}${item.sfx}`;
          later(() => setLines(prev => [...prev, { id: lid, text: empty, color: item.c || "#C9D1D9" }]), delay);
          for (let f = 1; f <= item.n; f++) {
            const fc  = f;
            const txt = `${item.l}${"█".repeat(fc)}${"░".repeat(item.total - fc)}${item.sfx}`;
            later(() => setLines(prev => prev.map(l => l.id === lid ? { ...l, text: txt } : l)), delay + fc * 26);
          }
          delay += item.n * 26 + 260;
        }
      });

      // Pause → fade → loop
      later(() => setFading(true),  delay + 2200);
      later(runSeq,                 delay + 2200 + 680);
    }

    later(runSeq, 700);
  }, [reduced, isMobile]);

  // Click opens the terminal easter egg
  const handleClick = () =>
    window.dispatchEvent(new CustomEvent("erpik:open-terminal"));

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <motion.div
      onClick={handleClick}
      whileHover={{ scale: 1.025, filter: "drop-shadow(0 48px 80px rgba(0,0,0,0.7))" }}
      transition={{ duration: 0.35 }}
      className="hidden md:block cursor-pointer select-none w-full max-w-[520px]"
      title="Click to open terminal"
      style={{ filter: "drop-shadow(0 32px 60px rgba(0,0,0,0.55))", perspective: "1200px" }}
      aria-hidden="true"
    >
      <div
        style={{
          transform: "rotateY(-5deg) rotateX(2deg)",
          transformStyle: "preserve-3d",
          opacity: fading ? 0 : 1,
          transition: "opacity 0.65s ease",
          borderRadius: 14,
          overflow: "hidden",
          border: "1px solid #30363D",
          boxShadow: "0 24px 80px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.04)",
        }}
      >
        {/* ── Title bar — matches Terminal.jsx exactly ── */}
        <div
          style={{
            background: "#161B22",
            borderBottom: "1px solid #21262D",
            borderRadius: "14px 14px 0 0",
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#FF5F57", display: "inline-block", flexShrink: 0 }} />
          <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#FEBC2E", display: "inline-block", flexShrink: 0 }} />
          <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#28C840", display: "inline-block", flexShrink: 0 }} />
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full bg-[#4ADE80] animate-ping opacity-60" />
              <span className="relative rounded-full h-1.5 w-1.5 bg-[#4ADE80]" />
            </span>
            <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: "#6E7681", letterSpacing: "0.14em" }}>
              sam@erpik — bash
            </span>
          </div>
        </div>

        {/* ── Terminal body ── */}
        <div
          style={{
            background: "#0D1117",
            padding: "16px 20px 22px",
            minHeight: 264,
            fontFamily: "ui-monospace, 'JetBrains Mono', 'SF Mono', 'Fira Code', monospace",
            fontSize: 13,
            lineHeight: 1.75,
          }}
        >
          {lines.map(line => (
            <div key={line.id} style={{ color: line.color, whiteSpace: "pre" }}>
              {line.text}
            </div>
          ))}
          {/* Block cursor */}
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 14,
              background: "#4ADE80",
              opacity: cursorOn ? 1 : 0,
              verticalAlign: "text-bottom",
              marginLeft: 1,
              transition: "opacity 0.08s",
            }}
          />
        </div>
      </div>
    </motion.div>
  );
}
